// Presentation formatter for the custom-size escape on the finished-size
// pickers (SizeGrid). When a customer types raw dimensions into the inline
// custom-size input ("8x9", "8.5 x 11", "5.25 by 8.25") we want the value to
// read back the same way the catalog sizes do — "8″ × 9″", matching the
// SIZE_CATALOG labels in the seed (e.g. "8.5″ × 11″"). This keeps the custom
// escape visually consistent with every other size chip instead of surfacing
// the bare text the customer typed.
//
// This is DISPLAY normalization only — no pricing or dimensional math (CLAUDE.md
// §"Pricing logic lives in the ERP"). The customer-typed value is the source of
// truth; we only tidy its glyphs (inch marks + multiplication sign).
//
// Conservative + idempotent by design:
//   - Only a clean two-number WxH input is reformatted. Anything else — a
//     three-axis "8 x 9 x 2", a note like "trim to oval", an empty string — is
//     returned UNCHANGED, so we never mangle free-form intent.
//   - Already-formatted input ("8″ × 9″") re-formats to itself, so it's safe to
//     run on blur (storage) AND again at read-back (older quotes / display).
//
// Accepted separators: x / X / × / "by". Accepted (optional) unit marks after
// either number: " / ″ / in / inch / inches. Decimals allowed (5.25). A
// leading-decimal input (".5x.75") is intentionally NOT matched — \d+ requires
// a digit before the decimal — so it falls through to the unchanged-return path
// like any other non-dimension text rather than widening the match surface.
const DIMENSION_RE =
  /^(\d+(?:\.\d+)?)\s*(?:"|″|in|inch|inches)?\s*(?:x|×|by)\s*(\d+(?:\.\d+)?)\s*(?:"|″|in|inch|inches)?$/i;

// Parse a clean two-number WxH custom-size input to its numeric dimensions in
// inches, or null when the text isn't a clean dimension pair (a three-axis
// "8 x 9 x 2", a note, an empty string — same conservative match surface as
// formatCustomSize). Used by the spec-summary mockup to take the custom size's
// aspect ratio for its thumbnail. Not pricing math (CLAUDE.md §"Pricing logic
// lives in the ERP") — it only shapes a preview.
export function parseCustomSize(raw: string): { width: number; height: number } | null {
  const m = DIMENSION_RE.exec(raw.trim());
  if (!m) return null;
  const width = Number(m[1]);
  const height = Number(m[2]);
  return width > 0 && height > 0 ? { width, height } : null;
}

export function formatCustomSize(raw: string): string {
  const text = raw.trim();
  if (text === '') return text;
  const m = DIMENSION_RE.exec(text);
  if (!m) return text;
  return `${m[1]}″ × ${m[2]}″`;
}
